<template>
    <div class="search">
        <div class="row no-gutters">
            <div class="col">
                
                <div class="filters pl-4 pr-4 d-flex justify-content-left align-items-center flex-wrap">
                    <template v-for="filter in $store.state.search.availableFilters">
                        <SearchFilter v-if="filter.type == ''"
                                      :key="filter.key"
                                      :title="filter.title" 
                                      :options="filter.groups" 
                                      v-bind="filter.attrs" 
                                      @save-filters="filtersSaved"
                                      class="mr-2 mb-2" />
                                      
                        <SearchFilterTemperature v-if="filter.type == 'temperature'"
                                                 :key="filter.key"
                                                 :title="filter.title" 
                                                 :options="filter.groups" 
                                                 @save-filters="filtersSaved"
                                                 class="mr-2 mb-2" />
                    </template>
                    
                    <a href="#" @click.prevent="clearAllFilters" v-if="Object.keys(filterValues).length">Clear All</a>
                </div>
                
                <div class="results" v-if="results.data && results.data.length > 0">
                    <Paginaton :data="results" 
                               v-on:pagination-change-page="performSearch"
                               :show-disabled="true"
                               :limit="5"
                               size="small"
                               align="center"
                               class="mt-5 mb-5"
                    >
                        <span slot="prev-nav"><fa-icon :icon="['fal', 'angle-double-left']" /></span>
                        <span slot="next-nav"><fa-icon :icon="['fal', 'angle-double-right']" /></span>
                    </Paginaton>
                    
                    <template v-if="results.meta && results.meta.from">
                        <div class="text-right text-muted mr-2" v-if="results.meta.total > results.meta.per_page"><small>Results {{ results.meta.from }} to {{ results.meta.to }} of {{ results.meta.total }}</small></div>
                        <div class="text-right text-muted mr-2" v-else><small>{{ results.meta.total }} results</small></div>
                    </template>
                    
                    <ol class="list-unstyled mb-0">
                        <li v-for="site in results.data" :key="site.id">
                            <SearchResult :site="site" 
                                          @data-changed="dataChanged"
                                          @mouseleave.native="resultUnhovered"
                                          @mouseenter.native="resultHovered(site)" />
                        </li>
                    </ol>
                    
                    <div class="my-5 pb-3 text-center">
                        <p class="text-muted mt-0 mb-2">Are we missing something?</p>
                        <BetterLink :to="{name: 'New Site'}" class="btn btn-sm btn-outline-primary text-dark">Submit a Hot Spring</BetterLink>
                    </div>
                    
                    <Paginaton :data="results" 
                               v-on:pagination-change-page="performSearch"
                               :show-disabled="true"
                               :limit="5"
                               size="small"
                               align="center"
                               class="mt-5 mb-5"
                    >
                        <span slot="prev-nav"><fa-icon :icon="['fal', 'angle-double-left']" /></span>
                        <span slot="next-nav"><fa-icon :icon="['fal', 'angle-double-right']" /></span>
                    </Paginaton>
                </div>
                
                <div v-else-if="!isSearching && $route.query.q != null && (!results || !results.data || !results.data.length)" style="height:75%;" class="p-4 d-flex flex-column align-items-center justify-content-center">
                    <h4 class="mb-5">Ooh! We didn't find anything for this search.</h4>
                    <h5 class="mb-4">Are we missing something? Why not submit it?</h5>
                    <BetterLink :to="{name: 'New Site'}" class="btn btn-primary">Submit a Hot Spring</BetterLink>
                </div>
                
                <div v-else-if="isSearching" style="height:75%;" class="p-4 d-flex flex-column align-items-center justify-content-center">
                    <div>
                        <div class="spinner-grow spinner-grow-sm" role="status">
                          <span class="sr-only">Loading...</span>
                        </div>
                        Searching ...
                    </div>
                </div>
            </div>
            
            <div class="col d-none d-sm-block">
                <ResultsMap :locations="resultsWithLocation" :focused-marker="focusedSiteId" />
            </div>
        </div>
    </div>
</template>

<script>
import { EventBus } from '@/eventBus'
import { mapState, mapGetters } from 'vuex'
import ObjectMixins from '@/mixins/ObjectMixins';
import SiteMixins from '@/mixins/SiteMixins';
import CurrencyMixins from '@/mixins/CurrencyMixins';
import SearchResult from '@/components/elements/SearchResult';
import ResultsMap from '@/components/elements/ResultsMap';
import SearchFilter from '@/components/elements/SearchFilter';
import SearchFilterTemperature from '@/components/elements/SearchFilterTemperature';
import Paginaton from 'laravel-vue-pagination'
// import VueScrollTo from 'vue-scrollto'

export default {
    name: 'Search',
    head: {
        title() {
            let title = "Search"
            if (this.$route.query.q) {
                title = `Search for "${this.$route.query.q}"`
                
                if (this.results && this.results.meta && this.results.meta.last_page > 1) {
                    title += ` (Page ${this.$route.query.page})`
                }
            }
            return {
                inner: title
            }
        },
    },
    mixins: [
        ObjectMixins,
        SiteMixins,
        CurrencyMixins,
    ],
    components: {
        SearchResult,
        ResultsMap,
        SearchFilter,
        SearchFilterTemperature,
        Paginaton,
    },
    data() {
        return {
            accessToken: process.env.VUE_APP_MAPBOX_ACCESS_TOKEN,
            mapStyle: process.env.VUE_APP_MAPBOX_MAP_STYLE,
            focusedSiteId: null,
        }
    },
    computed: {
        ...mapState('search', ['isSearching', 'terms', 'results']),
        ...mapGetters('search', ['filterValues']),
        resultsWithLocation() {
            return this.results && this.results.data && this.results.data.filter(result => result.location.coords != null) || []
        },
    },
    methods: {
        performSearch(page = 1) {
            // VueScrollTo.scrollTo('.search', 500)
            this.$store.dispatch('search/setPage', {page})
        },
        filtersSaved() {
            if (!this.objectsAreEqual(this.$store.state.search.filters, this.filterValues)) {
                this.$store.dispatch('search/setFilters', {filters: this.filterValues})
                this.$emit('updateHead')
            }
        },
        dataChanged(siteData) {
            let foundIndex = this.results.data.findIndex(fSite => fSite.id == siteData.id)
            this.results.data.splice(foundIndex, 1, siteData)
        },
        clearAllFilters() {
            EventBus.$emit('clear-all-filters');
        },
        resultHovered(site) {
            this.focusedSiteId = site.id
        },
        resultUnhovered() {
            this.focusedSiteId = null
        },
    },
    watch: {
        $route(to) {
            if (to.query.q != null) {
                this.$store.dispatch('search/setParams', {
                    terms: to.query.q,
                    filters: to.query.filters,
                    page: to.query.page,
                    updateRoute: false,
                })
            }
            this.$emit('updateHead')
        },
        results() {
            this.$emit('updateHead')
        },
    },
    created() {
        this.$store.commit('search/setStatus', true)
        if (this.$route.query.q != null) {
            this.$store.dispatch('search/setParams', {
                terms: this.$route.query.q,
                filters: this.$route.query.filters,
                page: this.$route.query.page,
                updateRoute: false,
            })
        }
        this.$emit('updateHead')
    },
};
</script>

<style lang="scss">
.search {
    margin-top: -24px;
    margin-bottom: -20px;
    
    .filters {
        padding: 15px 0;
    }
    
    .results {
        margin-top: -10px;
        margin-bottom: -16px;
    }
    
    #map {
        height: 100vh;
        position: sticky;
        top: 0;
    }
}

</style>